import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { SiteSettingsErrorPageComponent } from '@shared/components/error/site-settings-error-page/site-settings-error-page.component';
import {
  ClientWithExternalSiteConfig,
  ClientWithLocalSiteConfig,
} from '@shared/constants/site-config-client-exceptions';
import { ClientPath, castClientNameToClientPathEnum } from '@shared/models/client-path.enum';
import { SiteConfigInterface } from '@shared/models/site-config.interface';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { USER_SELECTED_SITE } from 'app.config';
import { DeepMergeLeafURI, deepmergeCustom } from 'deepmerge-ts';
import { BehaviorSubject, Observable, forkJoin, map, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { ISiteListDetails } from '../interfaces/site-settings.interfaces';

@Injectable({ providedIn: 'root' })
export class SiteConfigService {
  private baseUrl = '/assets/assets-branded';
  public sites: ISiteListDetails[] = [];

  public componentData: SiteConfigInterface = <any>{};
  public selfRegSitesCount: BehaviorSubject<number> = new BehaviorSubject(0);

  constructor(
    private httpClient: HttpClient,
    private snackBar: MatSnackBar
  ) {}

  public getComponentData(path: string) {
    if (!this.componentData || !this.componentData[path]) {
      return { default: {} };
    }
    return this.componentData[path];
  }

  public getAllComponentData(clientName: string): Promise<void> {
    const clientPath: ClientPath = castClientNameToClientPathEnum(clientName);

    return new Promise<void>((resolve) => {
      const defaultConfig$ = this.getDefaultConfig();
      const localConfig$ = this.getLocalConfig(clientPath);
      const externalConfig$ = this.getExternalConfig(clientPath);

      forkJoin([defaultConfig$, localConfig$, externalConfig$]).subscribe(
        ([defaultConfig, localConfig, externalConfig]: [
          SiteConfigInterface,
          SiteConfigInterface,
          SiteConfigInterface,
        ]) => {
          if (
            !Object.keys(defaultConfig).length &&
            !Object.keys(localConfig).length &&
            !Object.keys(externalConfig).length
          ) {
            this.handleError(clientPath);
          } else {
            const customDeepmerge = deepmergeCustom<{ DeepMergeArraysURI: DeepMergeLeafURI }>({ mergeArrays: false });
            this.componentData = customDeepmerge(this.componentData, defaultConfig, localConfig, externalConfig);
          }

          resolve();
        },
        (err: HttpErrorResponse) => this.handleError(clientPath, err)
      );
    });
  }

  private handleError(clientName: string, err: HttpErrorResponse = undefined) {
    console.error(`site configuration json not found for ${clientName}`);

    if (err) {
      console.error(err);
    }

    this.snackBar.openFromComponent(SiteSettingsErrorPageComponent, {
      duration: undefined,
      data: clientName,
    });
  }

  private getDefaultConfig(): Observable<Record<string, any>> {
    return this.httpClient.get(`${this.baseUrl}/default-site-config.json`);
  }

  private getLocalConfig(clientPath: ClientPath): Observable<Record<string, any>> {
    if (!ClientWithLocalSiteConfig.includes(clientPath)) return of({});

    return this.httpClient
      .get(`${this.baseUrl}/${clientPath}/component-data/site-config.json`)
      .pipe(catchError(() => of({})));
  }

  private getExternalConfig(clientPath: ClientPath): Observable<Record<string, any>> {
    if (!ClientWithExternalSiteConfig.includes(clientPath)) return of({});

    return this.httpClient.get(ApiEndpoint.SITE_CONFIG).pipe(catchError(() => of({})));
  }

  public get siteList(): Observable<ISiteListDetails[]> {
    if (this.sites?.length > 0) {
      this.selfRegSitesCount.next(this.sites?.length);
      return of(this.sites);
    }
    const allSites = this.httpClient.get(ApiEndpoint.SELF_REGISTRATION_SITES).pipe(
      take(1),
      map((sites: ISiteListDetails[]) => {
        this.sites = sites;
        this.selfRegSitesCount.next(this.sites?.length);
        return sites;
      })
    );
    return allSites;
  }

  public get selectedSite() {
    const site = this.siteList?.pipe(
      map((res: ISiteListDetails[]) =>
        res.find((item) => item.site_id === Number(localStorage.getItem(USER_SELECTED_SITE)))
      )
    );
    return site;
  }
}
